import React, { useState } from "react";
import { Alert, Card, Collapse, Descriptions, message, Modal, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { collection, doc, writeBatch } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { FIRESTORE_LEADS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import { LEAD_DEFAULT_STATUS } from "../../../../../constants/LeadConstant";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import dayjs from "dayjs";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";

const { Title, Text } = Typography;

const PreviewExcelLeadsModal = ({ visible, onCancel, onSuccess, validLeads = [], invalidLeads = [] }) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (validLeads.length === 0) {
      message.warning(t("no_valid_leads_to_import"));
      return;
    }

    setLoading(true);

    try {
      const timestamp = dayjs().unix();
      const leadsCollectionRef = collection(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_LEADS_TABLE,
      );

      // Firebase batch limit is 500 operations, so we need to split into batches
      const BATCH_SIZE = 500;
      let successCount = 0;
      let errorCount = 0;

      // Process leads in batches of 500
      for (let i = 0; i < validLeads.length; i += BATCH_SIZE) {
        const batchLeads = validLeads.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(firestore);

        batchLeads.forEach((lead) => {
          // Create a new document reference with auto-generated ID
          const newLeadRef = doc(leadsCollectionRef);

          const leadData = {
            ...lead,
            leadId: newLeadRef.id,
            region: COMPANY_COUNTRY_CODE,
            status: LEAD_DEFAULT_STATUS,
            createdAt: timestamp,
            createdBy: email,
            updatedAt: timestamp,
            updatedBy: email,
          };

          batch.set(newLeadRef, leadData);
        });

        try {
          await batch.commit();
          successCount += batchLeads.length;
        } catch (error) {
          console.error("Error saving batch:", error);
          errorCount += batchLeads.length;
        }
      }

      if (successCount > 0) {
        message.success(t("leads_imported_successfully", { count: successCount }));
        onSuccess();
        onCancel();
      }

      if (errorCount > 0) {
        message.warning(t("some_leads_failed_to_import", { count: errorCount }));
      }
    } catch (error) {
      console.error("Error importing leads:", error);
      message.error(t("notifications.error_importing_leads"));
    } finally {
      setLoading(false);
    }
  };

  const renderValidLeadHeader = (lead, index) => {
    return (
      <Space>
        <Text strong>
          #{index + 1} {lead.companyName}
        </Text>
        <Text type="secondary">
          {lead.contactFirstName} {lead.contactLastName}
        </Text>
        {lead.warnings && lead.warnings.length > 0 && (
          <Tag color="warning">
            {lead.warnings.length} {t("warnings")}
          </Tag>
        )}
      </Space>
    );
  };

  const renderInvalidLeadHeader = (item, index) => {
    return (
      <Space>
        <Text strong type="danger">
          Row #{item.row}: {item.lead.companyName || "(No company name)"}
        </Text>
        <Tag color="error">{item.errors.length} errors</Tag>
      </Space>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            {t("preview_leads_import")}
          </Title>
          <Tag color="success">{validLeads.length} valid</Tag>
          {invalidLeads.length > 0 && <Tag color="error">{invalidLeads.length} invalid</Tag>}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText={t("import_leads")}
      cancelText={t("cancel")}
      width={900}
      style={{ top: 20 }}
      confirmLoading={loading}
      okButtonProps={{
        disabled: validLeads.length === 0,
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{t("preview_leads_import_message")}</Text>
      </div>

      {/* Invalid Leads Section */}
      {invalidLeads.length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          <Alert
            message={t("invalid_leads_found")}
            description={t("fix_errors_in_excel_and_reupload")}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Typography.Title level={5} type="danger">
            {t("failed")} ({invalidLeads.length})
          </Typography.Title>
          <Collapse
            ghost
            accordion
            size="small"
            style={{ background: "transparent" }}
            items={invalidLeads.map((item, index) => ({
              key: index,
              label: renderInvalidLeadHeader(item, index),
              children: (
                <Card size="small" style={{ marginTop: 8, background: "#fff2f0" }}>
                  <Descriptions size="small" column={2} bordered>
                    <Descriptions.Item label={t("company_name")} span={2}>
                      <Text>{item.lead.companyName || "-"}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("contact_person")}>
                      <Text>
                        {item.lead.contactFirstName} {item.lead.contactLastName}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("email")}>
                      <Text>{item.lead.email || "-"}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("phone")}>
                      <Text>{item.lead.phone || "-"}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("city")}>
                      <Text>{item.lead.city || "-"}</Text>
                    </Descriptions.Item>
                  </Descriptions>

                  <div style={{ marginTop: 16 }}>
                    <Text strong type="danger">
                      {t("errors")}:
                    </Text>
                    <ul style={{ marginTop: 8, marginBottom: 0 }}>
                      {item.errors.map((error, errorIndex) => (
                        <li key={errorIndex}>
                          <Text type="danger">{error}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              ),
            }))}
          />
        </div>
      )}

      {/* Valid Leads Section */}
      {validLeads.length > 0 ? (
        <div>
          <Typography.Title level={5} type="success">
            {t("succeeded")} ({validLeads.length})
          </Typography.Title>
          <Collapse
            ghost
            accordion
            size="small"
            style={{ background: "transparent" }}
            items={validLeads.map((lead, index) => ({
              key: index,
              label: renderValidLeadHeader(lead, index),
              children: (
                <Card size="small" style={{ marginTop: 8, background: "#f6ffed" }}>
                  {lead.warnings && lead.warnings.length > 0 && (
                    <Alert
                      message={t("warnings")}
                      description={
                        <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                          {lead.warnings.map((warning, warningIndex) => (
                            <li key={warningIndex}>
                              <Text type="warning">{warning}</Text>
                            </li>
                          ))}
                        </ul>
                      }
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <Descriptions size="small" column={2} bordered>
                    <Descriptions.Item label={t("company_name")} span={2}>
                      <Text strong>{lead.companyName}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("contact_first_name")}>
                      <Text>{lead.contactFirstName}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("contact_last_name")}>
                      <Text>{lead.contactLastName}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("email")}>
                      <Text>{lead.email}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("phone")}>
                      <Text>{lead.phone}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("address")} span={2}>
                      <Text>{lead.address}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("city")}>
                      <Text>{lead.city}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("zip_code")}>
                      <Text>{lead.zipCode}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("country")}>
                      <Text>{lead.country}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("origin")}>
                      <Text>{lead.origin}</Text>
                    </Descriptions.Item>
                    {lead.website && (
                      <Descriptions.Item label={t("website")} span={2}>
                        <Text>{lead.website}</Text>
                      </Descriptions.Item>
                    )}
                    {lead.companyType && (
                      <Descriptions.Item label={t("company_type")}>
                        <Text>{lead.companyType}</Text>
                      </Descriptions.Item>
                    )}
                    {lead.deliveryInstructions && (
                      <Descriptions.Item label={t("delivery_instructions")} span={2}>
                        <Text>{lead.deliveryInstructions}</Text>
                      </Descriptions.Item>
                    )}
                    {lead.notes && (
                      <Descriptions.Item label={t("internal_notes")} span={2}>
                        <Text>{lead.notes}</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              ),
            }))}
          />
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Text type="secondary">{t("no_valid_leads_found")}</Text>
        </div>
      )}
    </Modal>
  );
};

export default PreviewExcelLeadsModal;
